import { useState } from 'react'
import { Button, Switch, TextInput, PasswordInput, PinInput, Modal, Select } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useTranslation } from 'react-i18next'
import { useSupabaseClient } from '@supabase/auth-helpers-react'

import Box from '@/components/ui/Box'
import ProfileManagePasskeys from '@/components/profiles/ProfileManagePasskeys'
import NotificationPreferences from '@/components/profiles/NotificationPreferences'
import ProfileAccountActions from '@/components/profiles/ProfileAccountActions'
import ProfileActivity from '@/components/profiles/ProfileActivity'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import useMe from '@/hooks/useMe'
import useWebPush from '@/hooks/useWebPush'

export default function ProfileSettings({ profile, isOwnProfile, onMutate }) {
    const { t } = useTranslation()
    const supabase = useSupabaseClient()
    const { requestPatch, requestPost } = useAxiosApi()
    const { isAdmin, realRole, debugRole } = useMe()
    const hasTechnicalEmail = profile.email?.endsWith('@accounts.baltic-watches.invalid')
    const webPush = useWebPush()

    // Role
    const [role, setRole] = useState(profile.role ?? 'MNP')
    const [roleLoading, setRoleLoading] = useState(false)

    const saveRole = async () => {
        setRoleLoading(true)
        try {
            await requestPost('/admin/update-user', {
                profile_id: profile.id,
                name: profile.name,
                short: profile.short,
                avatar: profile.avatar,
                color: profile.color,
                role,
            })
            notifications.show({ message: 'Rôle mis à jour', color: 'green' })
            onMutate?.()
        } catch {
            notifications.show({ message: 'Erreur lors de la mise à jour du rôle', color: 'red' })
        } finally {
            setRoleLoading(false)
        }
    }

    // Change name modal
    const [nameOpened, nameHandlers] = useDisclosure(false)
    const [name, setName] = useState(profile.name ?? '')
    const [saveLoading, setSaveLoading] = useState(false)

    // Change password modal
    const [passwordOpened, passwordHandlers] = useDisclosure(false)
    const [passwordLoading, setPasswordLoading] = useState(false)
    const [newPassword, setNewPassword] = useState('')
    const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')

    // Change PIN modal
    const [pinOpened, pinHandlers] = useDisclosure(false)
    const [pin, setPin] = useState('')
    const [pinLoading, setPinLoading] = useState(false)
    const [pinAction, setPinAction] = useState(null) // 'save' | 'remove'

    const areSamePasswords = newPassword === newPasswordConfirmation
    const isPasswordValid = isOwnProfile
        ? newPassword.length >= 10 && areSamePasswords
        : newPassword.length >= 10

    const baseUrl = isOwnProfile ? '/me' : `/profiles/${profile.id}`

    // Save name
    const saveProfile = async () => {
        setSaveLoading(true)
        try {
            await requestPatch(baseUrl, { name })
            notifications.show({
                message: 'Profil mis à jour',
                color: 'green',
            })
            nameHandlers.close()
            onMutate?.()
        } catch {
            // error handled by useAxiosApi
        } finally {
            setSaveLoading(false)
        }
    }

    // Change password
    const changePassword = async () => {
        setPasswordLoading(true)
        try {
            await requestPost(`${baseUrl}/password`, {
                password: newPassword,
            })
            notifications.show({
                message: 'Mot de passe modifié',
                color: 'green',
            })
            passwordHandlers.close()
        } catch {
            // error handled by useAxiosApi
        } finally {
            setPasswordLoading(false)
        }
    }

    // Save / remove PIN
    const savePin = async (value) => {
        setPinAction(value ? 'save' : 'remove')
        setPinLoading(true)
        try {
            await requestPost(`${baseUrl}/pin`, { pin: value })
            notifications.show({
                message: value ? 'Code PIN enregistré' : 'Code PIN supprimé',
                color: 'green',
            })
            pinHandlers.close()
            setPin('')
            onMutate?.()
        } catch {
            // error handled by useAxiosApi
        } finally {
            setPinLoading(false)
        }
    }

    // Sign out
    const [signOutLoading, setSignOutLoading] = useState(false)
    const signOut = async () => {
        setSignOutLoading(true)
        try {
            const { error } = await supabase.auth.signOut({ scope: 'local' })
            if (error) throw error
        } catch (error) {
            notifications.show({
                message: error?.message || t('error_server_message'),
                color: 'red',
            })
        } finally {
            setSignOutLoading(false)
        }
    }

    return (
        <>
            <div className="max-w-lg mx-auto p-6 flex flex-col gap-4">
                {/* Profil */}
                <Box title="Profil">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium">
                                    {profile.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {hasTechnicalEmail ? 'Sans email personnel · connexion par PIN' : profile.email}
                                </div>
                            </div>
                            <Button
                                variant="default"
                                size="xs"
                                onClick={() => {
                                    setName(profile.name ?? '')
                                    nameHandlers.open()
                                }}
                            >
                                Modifier le nom
                            </Button>
                        </div>

                        {isOwnProfile && (
                            <Button
                                fullWidth
                                color="dark"
                                variant="light"
                                loading={signOutLoading}
                                onClick={signOut}
                            >
                                {t('sign_out')}
                            </Button>
                        )}

                        {isOwnProfile && realRole === 'ADMIN' && (
                            <Button
                                fullWidth
                                variant="light"
                                color={debugRole ? 'orange' : 'gray'}
                                size="xs"
                                onClick={() => {
                                    if (debugRole) {
                                        localStorage.removeItem('debug_role')
                                    } else {
                                        localStorage.setItem('debug_role', 'MNP')
                                    }
                                    window.location.reload()
                                }}
                            >
                                {debugRole ? 'Désactiver la simulation MNP' : 'Simuler le rôle MNP'}
                            </Button>
                        )}
                    </div>
                </Box>

                {/* Role administration is restricted to other accounts. */}
                {isAdmin && !isOwnProfile && (
                    <Box title="Rôle">
                        <div className="flex items-end gap-3">
                            <Select
                                className="flex-1"
                                data={[
                                    { label: 'Administrateur', value: 'ADMIN' },
                                    { label: 'MNP', value: 'MNP' },
                                    ...(!hasTechnicalEmail ? [{ label: 'Revendeur', value: 'RETAILER' }] : []),
                                ]}
                                allowDeselect={false}
                                value={role}
                                onChange={setRole}
                            />
                            <Button
                                loading={roleLoading}
                                disabled={role === profile.role}
                                onClick={saveRole}
                            >
                                Enregistrer
                            </Button>
                        </div>
                    </Box>
                )}

                {(isAdmin || isOwnProfile) && <ProfileActivity profile={profile} />}
                {isAdmin && !isOwnProfile && (
                    <ProfileAccountActions profile={profile} onMutate={onMutate} />
                )}

                {/* Sécurité */}
                <Box title="Sécurité">
                    <div className="flex flex-col gap-3">
                        <Button
                            variant="default"
                            onClick={() => {
                                setNewPassword('')
                                setNewPasswordConfirmation('')
                                passwordHandlers.open()
                            }}
                        >
                            Changer le mot de passe
                        </Button>

                        <Button
                            variant="default"
                            onClick={() => {
                                setPin('')
                                pinHandlers.open()
                            }}
                        >
                            {profile.has_pin
                                ? 'Modifier le code PIN'
                                : 'Définir un code PIN'}
                        </Button>
                    </div>
                </Box>

                {/* Notifications — ADMIN uniquement */}
                {profile.notification_preferences && (
                    <NotificationPreferences
                        preferences={profile.notification_preferences}
                        baseUrl={baseUrl}
                        requestPost={requestPost}
                    />
                )}

                {/* Push notifications — propre navigateur uniquement */}
                {isOwnProfile && webPush.isSupported && (
                    <Box title="Notifications push">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm">
                                    Recevoir les notifications push sur ce navigateur
                                </div>
                                <div className="text-xs text-gray-400">
                                    Même quand le back-office est fermé
                                </div>
                            </div>
                            <Switch
                                checked={webPush.isSubscribed}
                                onChange={webPush.toggle}
                                disabled={webPush.loading}
                            />
                        </div>
                    </Box>
                )}

                {/* Clés d'accès — seulement pour son propre profil */}
                {isOwnProfile && (
                    <Box title="Clés d'accès">
                        <ProfileManagePasskeys
                            passkeys={profile.passkeys ?? []}
                            onMutate={onMutate}
                        />
                    </Box>
                )}
            </div>

            {/* Modal changement de nom */}
            <Modal
                opened={nameOpened}
                onClose={nameHandlers.close}
                title="Modifier le nom"
            >
                <div className="flex flex-col gap-3">
                    <TextInput
                        label={t('name')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                    />

                    <Button
                        disabled={!name.trim() || name === (profile.name ?? '')}
                        onClick={saveProfile}
                        loading={saveLoading}
                    >
                        {t('save')}
                    </Button>
                </div>
            </Modal>

            {/* Modal changement de mot de passe */}
            <Modal
                opened={passwordOpened}
                onClose={passwordHandlers.close}
                title="Changer de mot de passe"
            >
                <div className="flex flex-col gap-3">
                    <PasswordInput
                        label="Nouveau mot de passe"
                        description="10 caractères minimum"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                    />

                    {isOwnProfile && (
                        <PasswordInput
                            label="Confirmation du mot de passe"
                            autoComplete="new-password"
                            value={newPasswordConfirmation}
                            onChange={(e) =>
                                setNewPasswordConfirmation(e.target.value)
                            }
                        />
                    )}

                    <Button
                        disabled={!isPasswordValid}
                        onClick={changePassword}
                        loading={passwordLoading}
                    >
                        Enregistrer le nouveau mot de passe
                    </Button>
                </div>
            </Modal>

            {/* Modal code PIN */}
            <Modal
                opened={pinOpened}
                onClose={pinHandlers.close}
                size="md"
                title="Définir le code PIN de connexion rapide"
            >
                <div className="flex flex-col items-center gap-4">
                    <PinInput
                        value={pin}
                        onChange={setPin}
                        length={4}
                        type="number"
                        mask
                        autoFocus
                    />

                    <Button
                        loading={pinLoading && pinAction === 'save'}
                        disabled={pin.length !== 4}
                        fullWidth
                        onClick={() => savePin(pin)}
                    >
                        Sauvegarder le code PIN
                    </Button>

                    {profile.has_pin && !hasTechnicalEmail && (
                        <Button
                            variant="light"
                            color="red"
                            fullWidth
                            loading={pinLoading && pinAction === 'remove'}
                            onClick={() => savePin(null)}
                        >
                            Supprimer le code PIN
                        </Button>
                    )}
                </div>
            </Modal>
        </>
    )
}
